import Guid from "@/app/types/common/guid";

export interface BmGridColumnSortState {
  columnKey: string;
  descending: boolean;
}

export type GridExportFormat = "csv" | "pdf" | "googleSheets";

export interface BmGridExportEvent {
  filename: string;
  fileTitle: string;
  stateId: string;
  format: GridExportFormat;
}

export class BmGridState {
  // Whether the grid should use the default state. Set this to true to reset the
  // grid state back to the default grid state as defined by the column definitions.
  useGridDefault: boolean = false;

  // Ranked array of sort criteria to apply.
  sortBy: BmGridColumnSortState[] = [];

  // Ranked array of groupings to apply.
  groupBy: string[] = [];

  // The state of the applied column filters
  columnFilters: { [columnKey: string]: any } = {};

  removedColumnKeys: string[] = [];

  columnOrder: string[] = [];

  gridSearchFilter: string = "";

  stateId: string;

  columnPixelWidthMap = new Map<string, number>();

  public constructor(state?: Partial<BmGridState>) {
    if (!state) {
      this.useGridDefault = true;
    } else {
      Object.assign(this, state || {});
    }
    this.stateId = state?.stateId || Guid.create().toString();
  }

  /**
   * Resets the grid state to indicate that the grid should use the default (as
   * defined by the column definitions).
   */
  public reset() {
    this.sortBy = [];
    this.groupBy = [];
    this.columnFilters = {};
    this.removedColumnKeys = [];
    this.columnOrder = [];
    this.gridSearchFilter = "";
    this.useGridDefault = true;
    this.columnPixelWidthMap = new Map<string, number>();
  }

  /**
   * Set or clear a column filter.
   * Reassigns columnFilters for dual-compatible Vue 2/3 reactivity on dynamic keys.
   * Pass a falsy value (e.g. undefined) to remove the filter for key.
   */
  public setFilter(key: string, value?: unknown) {
    const next: { [columnKey: string]: unknown } = {};
    for (const existingKey of Object.keys(this.columnFilters)) {
      if (existingKey !== key) {
        next[existingKey] = this.columnFilters[existingKey];
      }
    }
    if (value) {
      next[key] = value;
    }
    this.columnFilters = next;
  }

  /**
   * Applies a new column sort to the existing sort state. I have borrowed this logic from
   * ag-grid (ascend, descend, remove + stacking). https://www.ag-grid.com/
   *
   * @param columnKey The column key being sorted
   * @param stack Whether to stack this on to the existing sort state or not.
   */
  applyColumnSort(columnKey: string, stack: boolean = false): void {
    const currentColumnSort = this.sortBy.find(
      (sortBy) => sortBy.columnKey === columnKey
    );

    if (stack) {
      if (currentColumnSort) {
        if (currentColumnSort.descending) {
          // Was descending, so remove it
          this.sortBy = this.sortBy.filter(
            (sortBy) => sortBy.columnKey !== columnKey
          );
        } else {
          // Was ascending, so make it descending
          currentColumnSort.descending = true;
        }
      } else {
        this.sortBy = this.sortBy.concat({
          columnKey,
          descending: false,
        });
      }
    } else if (currentColumnSort?.descending) {
      this.sortBy = [];
    } else {
      this.sortBy = [
        {
          columnKey,
          descending: !!currentColumnSort,
        },
      ];
    }
  }

  applyExternalSorting(sortStatus: BmGridColumnSortState[]): void {
    this.sortBy = sortStatus;
  }

  applyColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  setColumnPixelWidth(key: string, pixels: number) {
    this.columnPixelWidthMap.set(key, pixels);
  }
}
